import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from "@mui/material";
import { get, put } from "src/services/httpClient.service";
import { useSnackbar } from "src/components/snackbar/useSnackbar";

/* =========================
   Interface
========================= */
interface ThirdPartyApp {
  id: string;
  appName: string;
  appType: string;
  isActive: boolean;
}

interface Props {
  handleClose: () => void;
  fetchApps: () => void;
  thirdAppId: any;
}

const UpdateThirdPartyApp = ({
  handleClose,
  fetchApps,
  thirdAppId
}: Props) => {
  const { showSnackbar } = useSnackbar();
  const APIURL = process.env.REACT_APP_BASE_API_URL;

  const [formData, setFormData] = useState<ThirdPartyApp | null>(null);
  const [loading, setLoading] = useState(false);

  const APP_TYPES = [
    { label: "Watcho", value: "WATCHO" },
    { label: "SonyLIV", value: "SONYLIV" },
    { label: "ZEE5", value: "ZEE5" },
    { label: "Sun NXT", value: "SUNNXT" },
    { label: "Disney+ Hotstar", value: "HOTSTAR" },
    { label: "Amazon Prime", value: "PRIME" }
  ];

  /* =========================
     Fetch & Filter App
  ========================= */
  const fetchAppById = async (thirdAppId) => {
    try {
      const response = await get(`${APIURL}/api/third-party-apps/getById`, {params: {id: thirdAppId}});
      setFormData(response?.data);
      // if (Array.isArray(response?.data)) {
      //   const selectedApp = response.data.find(
      //     (item: ThirdPartyApp) => item.id === thirdAppId.id
      //   );

      //   if (selectedApp) {
      //     setFormData(selectedApp);
      //   } else {
      //     showSnackbar("App not found", "error");
      //     handleClose();
      //   }
      // }
    } catch (error) {
      showSnackbar("Failed to fetch app details", "error");
      handleClose();
    }
  };

  useEffect(() => {
    if (thirdAppId) {
      fetchAppById(thirdAppId);
    }
  }, [thirdAppId]);

  /* =========================
     Handlers
  ========================= */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) =>
      prev ? { ...prev, [name]: value } : prev
    );
  };

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) =>
      prev ? { ...prev, isActive: e.target.checked } : prev
    );
  };

  const handleUpdate = async () => {
    console.log(formData)
    if (!formData) return;

    const payload = {
      id: formData.id,
      appName: formData.appName,
      appType: formData.appType,
      isActive: formData.isActive
    };
    console.log(payload)
    try {
      setLoading(true);

      await put(`${APIURL}/api/third-party-apps/updateThirdPartyApp/${formData.id}`, payload);

      showSnackbar("App updated successfully", "success");
      fetchApps();
      handleClose();
    } catch (error: any) {
      showSnackbar(
        error?.message || "Failed to update app",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!formData) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  /* =========================
     UI
  ========================= */
  return (
    <Box sx={{ p: 3, maxWidth: 500 }}>
      <TextField
        fullWidth
        label="App Name"
        name="appName"
        value={formData.appName}
        onChange={handleChange}
        margin="normal"
      />

      <FormControl fullWidth margin="normal">
        <InputLabel>App Type</InputLabel>

        <Select
          name="appType"
          value={formData.appType}
          label="App Type"
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              appType: e.target.value
            }))
          }
        >
          {APP_TYPES.map((app) => (
            <MenuItem key={app.value} value={app.value}>
              {app.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Switch
            checked={formData.isActive}
            onChange={handleToggle}
          />
        }
        label="Active"
      />

      <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleUpdate}
          disabled={loading}
        >
          {loading ? "Updating..." : "Update"}
        </Button>

        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default UpdateThirdPartyApp;
